
import { defineComponent, computed, ref, watch } from "vue";
import { trans } from "@/globals/translator";
import useMedias from "@/composables/useMedias";
import { useHead } from "@vueuse/head";
import { useRouter, useRoute } from "vue-router";
import { getCurrentDate } from "@/utils/dateTimeFormatter";
import { useIntersectionObserver } from "@vueuse/core";

// Components
import Grid from "@moonlight/vue3/components/Grid.vue";
import Row from "@moonlight/vue3/components/Row.vue";
import Col from "@moonlight/vue3/components/Col.vue";
import DropdownListGroup from "@moonlight/vue3/components/DropdownListGroup.vue";
import DropdownChevronIcon from "@moonlight/vue3/components/dropdown/DropdownChevronIcon.vue";
import PosterCard from "@/components/media/PosterCard.vue";
import PosterCardSkeleton from "@/components/media/PosterCardSkeleton.vue";
import PageTitle from "@/components/common/PageTitle.vue";
import Button from "@moonlight/vue3/components/Button.vue";
import ButtonText from "@moonlight/vue3/components/button/ButtonText.vue";
import ButtonIcon from "@moonlight/vue3/components/button/ButtonIcon.vue";

const LIMIT = 60;

export default defineComponent({
  components: {
    Grid,
    Row,
    Col,
    DropdownListGroup,
    DropdownChevronIcon,
    PosterCard,
    PosterCardSkeleton,
    PageTitle,
    Button,
    ButtonText,
  },
  setup() {
    useHead({
      title: trans("general.window_title_template", {
        current_page: trans("general.cinema_premieres"),
      }) as string,
    });
    const router = useRouter();
    const route = useRoute();
    const { medias, fetchMedias, requestStateMedias, resetMedias, isLastPage } =
      useMedias();

    const endOfPageRef = ref<HTMLDivElement>();

    // Media dates filter options

    const mediaDatesOptions = computed(() => {
      return [
        {
          value: "coming-soon",
          name: trans("general.coming_soon"),
        },
        {
          value: "previous",
          name: trans("general.previous"),
        },
      ];
    });

    const isOpenFilterMediaDates = ref(false);

    const filterDate = computed({
      set(value: string) {
        router.push({
          query: {
            ...route.query,
            date: value,
            page: 1,
          },
        });
      },
      get() {
        return (
          (route.query.date as string) || mediaDatesOptions?.value?.[0]?.value
        );
      },
    });

    watch(
      () => route.query,
      (value, oldValue) => {
        if (Object.keys(value).length > 0) {
          let dateFrom = undefined;
          let dateTo = undefined;
          let order = "asc";

          if (value.date === "previous") {
            order = "desc";
            dateTo = getCurrentDate().format("YYYY-MM-DD");
          } else if (value.date === "coming-soon") {
            dateFrom = getCurrentDate().format("YYYY-MM-DD");
          } else {
            dateFrom = (value.date as string)?.split("/")[0];
            dateTo = (value.date as string)?.split("/")[1];
          }

          if (value.date !== oldValue?.date) {
            resetMedias();
          }
          if (value.date !== oldValue?.date || value.page !== oldValue?.page) {
            fetchMedias({
              page: parseInt(value.page as string),
              limit: LIMIT,
              dateFrom,
              dateTo,
              order,
              type: "cine",
            });
          }
        } else if (route.name === "CineMedias") {
          setDefaultFilters();
        }
      },
      { immediate: true, deep: true }
    );

    // Load next page when reaching it
    useIntersectionObserver(endOfPageRef, ([{ isIntersecting }]) => {
      if (!isLastPage.value) {
        if (isIntersecting && requestStateMedias.value.status !== "loading") {
          router.replace({
            query: {
              ...route.query,
              page: Number(route.query.page) + 1,
            },
          });
        }
      }
    });

    function setDefaultFilters() {
      router.push({
        query: {
          date: filterDate.value,
          page: 1,
        },
      });
    }

    return {
      isOpenFilterMediaDates,
      trans,
      mediaDatesOptions,
      requestStateMedias,
      medias,
      ButtonIcon,
      endOfPageRef,
      LIMIT,
      filterDate,
    };
  },
});
